/*
 * Head office, franchises and the security center (draft 11): what the routes take and answer.
 */
import { z } from "zod";
import { ROLES } from "./core.js";
import { companyLevelSchema } from "./levels.js";
import { ADDONS } from "./franchise.js";
import { locationLine } from "./settings.js";
import { addressParts } from "./address.js";

const zip = z.string().regex(/^\d{5}$/, "Use 5 digits for a ZIP code.");
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a day like 2026-11-01.");
const cents = z.number().int().min(0).max(10_000_000);

// Dashboards ----------------------------------------------------------------------------------------------------------

const levelNumbers = {
  collected30Cents: z.number(),
  collected12Cents: z.number(),
  customersServed: z.number(),
  repeatingClients: z.number(),
  newThisMonth: z.number(),
  repeatingMonthlyCents: z.number(),
};

/** One company below on the Head office page. runBy says whether a franchise owner or head office runs it. */
export const overviewRow = z.object({
  companyId: z.uuid(),
  name: z.string(),
  parentId: z.uuid().nullable(),
  level: companyLevelSchema,
  status: z.enum(["active", "paused"]),
  runBy: z.enum(["franchise", "hq"]),
  ownerName: z.string().nullable(),
  zips: z.number(),
  billStatus: z.enum(["paid", "open", "processing", "failed"]).nullable(),
  namesPrivate: z.boolean(),
  /** The company's own brand color (#RRGGBB), for its bars and dots on HQ Home. */
  brandColor: z.string().nullable(),
  /** A location's sub locations (its own lines and ZIPs, like Metairie under New Orleans), from its message settings. */
  subLocations: z.array(
    z.object({
      name: z.string(),
      /** How people read it: "The Cleaners of Metairie". */
      fullName: z.string(),
      phone: z.string(),
      zips: z.array(z.string()),
      email: z.string().nullable(),
      address: addressParts.nullable(),
    }),
  ),
  ...levelNumbers,
});
export type OverviewRow = z.infer<typeof overviewRow>;

export const overviewAnswer = z.object({
  level: companyLevelSchema,
  totals: z.object({
    ...levelNumbers,
    locations: z.number(),
    franchises: z.number(),
    billsFailed: z.number(),
    reviewOverdue: z.boolean(),
  }),
  /**
   * Money collected each month for the last 12 months, every company below added up (oldest first). byCompany splits it
   * by company id (this one and every one below), for the chart colored by brand.
   */
  months: z.array(z.object({ month: z.string(), collectedCents: z.number(), byCompany: z.record(z.string(), z.number()) })),
  rows: z.array(overviewRow),
});
export type OverviewAnswer = z.infer<typeof overviewAnswer>;

// Territories ---------------------------------------------------------------------------------------------------------

export const territoryRow = z.object({
  companyId: z.uuid(),
  name: z.string(),
  parentId: z.uuid().nullable(),
  level: companyLevelSchema,
  franchise: z.boolean(),
  zips: z.array(z.string()),
  /** The cities those ZIPs are in, each with its ZIPs, most ZIPs first ("Other" for a ZIP not on the list). */
  areas: z.array(z.object({ city: z.string(), state: z.string(), zips: z.array(z.string()) })),
  /** About how far out it serves: the farthest ZIP from the middle of them all, in miles, rounded up to 5. */
  radiusMiles: z.number().nullable(),
});
export type TerritoryRow = z.infer<typeof territoryRow>;
/** Head office sets a location's sub locations: the whole list, in order. */
export const subLocationsPutBody = z.object({ lines: z.array(locationLine).max(10) });

export const territoriesAnswer = z.object({ rows: z.array(territoryRow) });
export const territoryPutBody = z.object({ zips: z.array(zip).max(2000) });

export const placeBody = z.object({
  zip: z.string().trim().max(10).optional(),
  address: z.string().trim().max(300).optional(),
  companyId: z.uuid().optional(),
});
export const placeAnswer = z.object({
  companyId: z.uuid(),
  name: z.string(),
  how: z.enum(["picked", "zip", "home"]),
  /** No location lists this ZIP, so it went to the home location. */
  outside: z.boolean(),
});
export type PlaceAnswer = z.infer<typeof placeAnswer>;

// People across companies ---------------------------------------------------------------------------------------------

export const twoStepStateSchema = z.enum(["app", "not_set_up", "not_asked"]);

export const membershipRow = z.object({
  companyId: z.uuid(),
  companyName: z.string(),
  role: z.enum(ROLES),
  active: z.boolean(),
});
export const hqPersonRow = z.object({
  userId: z.uuid(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  /** Turned off: no sign in anywhere. */
  disabled: z.boolean(),
  twoStep: twoStepStateSchema,
  lastActiveAt: z.string().nullable(),
  memberships: z.array(membershipRow),
});
export type HqPersonRow = z.infer<typeof hqPersonRow>;
export const membershipPutBody = z.object({ role: z.enum(ROLES) });

// Franchise plans and bills -------------------------------------------------------------------------------------------

export const addonSchema = z.enum(ADDONS);
export const addonRow = z.object({
  addon: addonSchema,
  on: z.boolean(),
  priceCents: z.number(),
  allowance: z.number(),
  extraCents: z.number(),
  usedThisMonth: z.number(),
});
export type AddonRow = z.infer<typeof addonRow>;

export const billRow = z.object({
  id: z.uuid(),
  month: z.string(),
  feeCents: z.number(),
  addonsCents: z.number(),
  extraCents: z.number(),
  totalCents: z.number(),
  /** processing: a bank payment (ACH) on its way, a few days. */
  status: z.enum(["open", "processing", "paid", "failed"]),
  tries: z.number(),
  failure: z.string().nullable(),
  paidAt: z.string().nullable(),
});
export type BillRow = z.infer<typeof billRow>;

export const planAnswer = z.object({
  companyId: z.uuid(),
  name: z.string(),
  /** none: not a franchise (head office runs it, no fee). */
  status: z.enum(["none", "active", "ended"]),
  feeCents: z.number(),
  startedOn: z.string().nullable(),
  hqSeesNames: z.boolean(),
  ownerName: z.string().nullable(),
  /** The card or bank account the fee is paid from (bank payments, owner answer 107). */
  card: z.object({ brand: z.string(), last4: z.string(), bank: z.boolean() }).nullable(),
  /** Paid add-ons are paused: a bill has gone unpaid 30 days (answer 103). */
  addonsPaused: z.boolean(),
  addons: z.array(addonRow),
  bills: z.array(billRow),
});
export type PlanAnswer = z.infer<typeof planAnswer>;
export const franchiseRow = planAnswer;

export const planPutBody = z
  .object({
    status: z.enum(["active", "ended"]),
    feeCents: cents,
    startedOn: day,
    hqSeesNames: z.boolean(),
    addons: z
      .array(
        z.object({
          addon: addonSchema,
          on: z.boolean(),
          priceCents: cents.optional(),
          allowance: z.number().int().min(0).max(1_000_000).optional(),
          extraCents: cents.optional(),
        }),
      )
      .max(ADDONS.length),
  })
  .partial()
  .strict();
export type PlanPut = z.infer<typeof planPutBody>;

export const cardSetupAnswer = z.object({
  setupId: z.string(),
  clientSecret: z.string(),
  publishableKey: z.string(),
  /** No real Stripe here: saving uses the test card 4242. */
  fake: z.boolean(),
});
export const cardConfirmBody = z.object({ setupId: z.string().min(3).max(200) });

// Security center -----------------------------------------------------------------------------------------------------

export const reviewState = z.object({
  lastAt: z.string().nullable(),
  byName: z.string().nullable(),
  dueOn: z.string(),
  overdue: z.boolean(),
});
export type ReviewState = z.infer<typeof reviewState>;

export const deviceRow = z.object({
  id: z.uuid(),
  kind: z.enum(["web", "mobile"]),
  device: z.string(),
  ip: z.string().nullable(),
  signedInAt: z.string(),
  lastUsedAt: z.string(),
  twoStepPassed: z.boolean(),
});
export const securityLogin = z.object({
  userId: z.uuid(),
  name: z.string(),
  group: z.enum(["owners", "office", "crew"]),
  roles: z.array(z.object({ companyName: z.string(), role: z.enum(ROLES) })),
  twoStep: twoStepStateSchema,
  lastActiveAt: z.string().nullable(),
  idleDays: z.number().nullable(),
  devices: z.array(deviceRow),
  /** Sign out and Remove access work on this login (not yourself, not someone above your role). */
  canAct: z.boolean(),
});
export type SecurityLogin = z.infer<typeof securityLogin>;
export const securityAnswer = z.object({ review: reviewState, logins: z.array(securityLogin) });
export type SecurityAnswer = z.infer<typeof securityAnswer>;
export const signOutAllAnswer = z.object({ logins: z.number(), devices: z.number() });

/** A client signed in to My Trades (the client hub), with the clients here and below that share the phone. */
export const hubSignIn = z.object({
  /** The newest device's id, to name the row. */
  key: z.uuid(),
  clients: z.array(z.object({ clientId: z.uuid(), name: z.string(), companyName: z.string() })),
  phoneEnd: z.string(),
  lastSeenAt: z.string(),
  devices: z.array(
    z.object({
      id: z.uuid(),
      device: z.string(),
      ip: z.string().nullable(),
      signedInAt: z.string(),
      lastSeenAt: z.string(),
      /** Opened from an invoice or quote link: it can look but not change anything until a code. */
      lookOnly: z.boolean(),
    }),
  ),
});
export type HubSignIn = z.infer<typeof hubSignIn>;
export const hubSignOutBody = z.object({
  sessionId: z.uuid(),
  /** Every device of that phone, not just this one. */
  all: z.boolean().default(false),
});

export const activityQuery = z.object({
  companyId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export const activityRow = z.object({
  id: z.uuid(),
  at: z.string(),
  companyId: z.uuid(),
  companyName: z.string(),
  userId: z.uuid().nullable(),
  userName: z.string().nullable(),
  action: z.string(),
  subjectType: z.string().nullable(),
  subjectId: z.string().nullable(),
  ip: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
});
export type ActivityRow = z.infer<typeof activityRow>;

/**
 * The owner's Obsidian vault as a graph for the HQ Home brain card: one node per note (its folder is the group), one link
 * per [[note]] link between two notes. Made on the owner's PC and copied to the server; only the one login named on the
 * server ever gets it.
 */
export const vaultGraphAnswer = z.object({
  madeAt: z.string(),
  /** edited: when the note last changed (ms), so the card fires the notes being worked on. */
  nodes: z.array(z.object({ label: z.string(), group: z.string(), edited: z.number().optional() })),
  /** Index pairs into nodes. */
  links: z.array(z.tuple([z.number().int(), z.number().int()])),
});
export type VaultGraphAnswer = z.infer<typeof vaultGraphAnswer>;
